// [PURPOSE]: Curated vocabularies for the Custom Subclass Effect Builder UI.
// [WHY]: The Content Workshop subclass creator needs dropdowns whose options match
//   the effect-registry contract used by srdSubclassFeatures.js / effectCalculator.
//   Pulling these out of the component file keeps the UI lean and lets us extend
//   the pickers (e.g., add a new condition flag) by editing one place.
// [INTEGRATION]: Imported by CustomSubclassCreator.js. The names of the operations
//   here are the SAME strings the registry consumes — do not localize.
// [Phase 4b BC-12 — APR 2026]: Companion to the Effect Builder UI.

import { DAMAGE_TYPES } from '../constants/enums';

/**
 * Effect types the Effect Builder exposes. The order here is the order the
 * dropdown will show. `description` is shown as a hint under the picker.
 *
 * Curated to mirror what the effect registry consumes. See
 * docs/superpowers/specs/2026-04-25-cw-subclass-effect-builder-gaps.md §
 * "Reference vocabularies".
 *
 * [Phase 5 Track B]: perRestSpell, atWillSpell, ritualSpell added.
 * [BATCH 10d / P1 D-18 — APR 2026]: learnedSpell, spellModifier, and
 *   hitDieRecovery added. Pre-fix the comment said these "would be
 *   foot-guns at the CW level" — that was true before Phase 4b, but the
 *   runtime support has since landed (effectCalculator.js:1956 / 2923 /
 *   3274 are the consumers) and a CW author cannot reproduce SRD content
 *   like Durable (hitDieRecovery), Magic Initiate (learnedSpell), or
 *   Spell Sniper (spellModifier on range) without them.
 */
export const EFFECT_TYPES = Object.freeze([
  { value: 'criticalRange',   label: 'Critical Range',   description: 'Lower the natural roll needed to crit (e.g., 19 for Improved Critical).' },
  { value: 'damageBonus',     label: 'Damage Bonus',     description: 'Flat or dice-based damage bonus (e.g., +2, +1d8 radiant).' },
  { value: 'attackBonus',     label: 'Attack Bonus (universal)',  description: 'Bonus to-hit on ALL attack rolls (weapon AND spell). For weapon-only or spell-only bonuses, use weaponAttackBonus or spellAttackBonus.' },
  { value: 'weaponAttackBonus', label: 'Weapon Attack Bonus',      description: 'Bonus to-hit on weapon attack rolls only (does not buff spell attacks). Use for items like +1 weapons or Bracers of Archery.' },
  { value: 'spellAttackBonus',  label: 'Spell Attack Bonus',       description: 'Bonus to-hit on spell attack rolls only (does not buff weapon attacks). Use for items like Wand of the War Mage.' },
  { value: 'damageReduction', label: 'Damage Reduction', description: 'Subtract a flat amount from incoming damage of a given type.' },
  { value: 'ac',              label: 'AC',               description: 'Bonus or override to Armor Class.' },
  { value: 'hp',              label: 'HP',               description: 'Bonus to maximum hit points (formulas allowed, e.g., sorcererLevel*1).' },
  { value: 'speed',           label: 'Speed',            description: 'Bonus or override to walking speed.' },
  { value: 'initiative',      label: 'Initiative',       description: 'Bonus to initiative rolls.' },
  { value: 'spellDC',         label: 'Spell Save DC',    description: 'Bonus to spell save DC.' },
  { value: 'carryCapacity',   label: 'Carry Capacity',   description: 'Multiplier on carrying / lift / push / drag.' },
  { value: 'resistance',      label: 'Resistance',       description: 'Resistance to a damage type. Use the value field to pick the type.' },
  { value: 'sense',           label: 'Sense',            description: 'Grant a sense (darkvision, blindsight, tremorsense, truesight).' },
  { value: 'proficiency',     label: 'Proficiency',      description: 'Grant proficiency in a skill / save / tool / weapon / armor.' },
  { value: 'advantage',       label: 'Advantage',        description: 'Grant advantage on a roll category (save:charm, attack:vowed).' },
  { value: 'feature',         label: 'Feature Flag',     description: 'Flag id consumed by UI / combat code (e.g., reckless_attack).' },
  { value: 'skill',           label: 'Skill',            description: 'Bonus to a single skill. Pair with a target like skill:perception.' },
  { value: 'extra_attack',    label: 'Extra Attack',     description: 'Add another attack on the Attack action (operation: add).' },
  { value: 'perRestSpell', label: 'Per-Rest Spell',  description: 'Grant a spell castable N times per rest (fixed count or ability-modifier formula). Toggle "Picked at level-up" so the player chooses the spell at a class level instead of you naming it (Wizard Signature Spells L20 pattern).' },
  { value: 'atWillSpell',  label: 'At-Will Spell',   description: 'Grant a spell castable at will (no slot, no uses limit). Toggle "Picked at level-up" so the player chooses (Wizard Spell Mastery L18 pattern).' },
  { value: 'ritualSpell',  label: 'Ritual Spell',    description: 'Grant a spell castable as a ritual even if not on class spell list.' },
  // [Spec A Task 10 — 2026-05-16]: Generic slot-recovery action.
  //   Wizard Arcane Recovery is the SRD use; CW custom-class slot-recovery
  //   features use the same shape. Value is an OBJECT (not string) with
  //   actionName / budgetFormula / maxSlotTier / spellSlotPool / classKey /
  //   poolResourceId. RestPanel "Class Actions" section iterates these via
  //   effectCalculator.getSlotRecoveryActions.
  { value: 'slotRecoveryAction', label: 'Slot Recovery Action', description: 'Invokable button (RestPanel Class Actions) that recovers expended spell slots from a pool. Player allocates a budget (e.g., ceil(classLevel/2) for Wizard) across slot tiers up to a cap.' },
  // [Spec B Task 14 — 2026-05-16]: Three new action-surface types.
  //   spendActionMenu: SRD use is Monk's Ki Spend Menu — a curated list of
  //     "spend N resource → do thing" options attached to a class resource
  //     (ki_points / sorcery_points / etc). Surfaces in ClassResourcesPanel
  //     "Spend Options" expandable pane via effectCalculator.getSpendActionMenus.
  //   reactionTrigger: SRD use is Artificer Flash of Genius — a reaction-style
  //     ability invoked outside the normal turn loop. Surfaces in StatsPanel
  //     "Reactions" section via effectCalculator.getReactionTriggers; click
  //     Invoke decrements the attachedToResource pool by `cost`.
  //   longRestPicker: SRD use is Artificer Infuse Item / Spell-Storing Item —
  //     a post-long-rest picker that lets the player bind chosen options
  //     (infusion → item, spell → item, etc) to inventory targets. Iterated
  //     after long rest by DailySetupModal via effectCalculator.getLongRestPickers.
  { value: 'spendActionMenu',  label: 'Spend Action Menu',  description: 'Curated list of "spend N pool resource → do thing" options. Surfaces in ClassResourcesPanel "Spend Options" pane attached to the chosen pool. Monk Ki Spend Menu is the SRD example.' },
  { value: 'reactionTrigger',  label: 'Reaction Trigger',   description: 'Invokable reaction ability surfaced in StatsPanel "Reactions" section. Click Invoke decrements the attached pool by `cost`. Artificer Flash of Genius is the SRD example.' },
  { value: 'longRestPicker',   label: 'Long-Rest Picker',   description: 'Post-long-rest picker (DailySetupModal) that binds chosen options (infusions / spells / boons) to inventory targets. Artificer Infuse Item / Spell-Storing Item are the SRD examples.' },
  // [Spec C Task 1 — 2026-05-16/17]: triggeredEffect is the 7th generic effect
  //   type. Fires automatically when a named game event occurs (attack hits,
  //   save is rolled, damage is taken, rest completes). Value is an OBJECT
  //   with triggerId (from src/data/triggerVocabulary.js TRIGGER_VOCABULARY) +
  //   optional condition object + payload[] of nested effect rows. Closes the
  //   5 inert magical-ammo fields, Mind Sharpener, Spell-Refueling Ring,
  //   Vicious Weapon, Flame Tongue. See docs/superpowers/specs/2026-05-16-triggered-effect-design.md.
  { value: 'triggeredEffect',  label: 'Triggered Effect',   description: 'Fires automatically when a named game event occurs (on-hit, on-crit, on-save-rolled, on-take-damage, on-rest-completed). Payload is an array of effect-rows resolved at fire-time. Arrow of Slaying / Flame Tongue / Mind Sharpener are SRD examples.' },
  // [BATCH 10d / P1 D-18 — APR 2026]
  { value: 'learnedSpell',    label: 'Learned Spell',    description: 'Add a spell to the character’s known list. Casting still costs a slot.' },
  { value: 'spellModifier',   label: 'Spell Modifier',   description: 'Modify a target spell’s range / damage / DC at cast time. Use target = spell name.' },
  { value: 'hitDieRecovery',  label: 'Hit Die Recovery', description: 'Grant extra HP back when spending hit dice on a short rest (e.g., Durable).' },
  // [PHASE B / #028 — 2026-05-06]: causeCondition closes B1-6 — items
  //   like "on hit, target makes a DC 15 CON save or be Restrained" now
  //   author through the same effect-builder schema as everything else.
  //   Effect row carries trigger / dc / saveType / duration alongside
  //   the existing type/operation/value/target/condition fields. The
  //   value field picks from SRD_CONDITION_OPTIONS (`src/data/srdConditions.js`).
  // [ESR-001 FIX — MAY 2026]: Hidden from CW UI for v1.0 because there is
  //   no consumer in src/services/* that fires causeCondition effects (no
  //   on-hit hook, no save-resolution dispatch). CW authors creating
  //   "paralyzing weapon" effects would silently never trigger. Restore in
  //   v1.0.1 once the runtime consumer lands. Definition kept here so saved
  //   data round-trips intact when the consumer is wired.
  // { value: 'causeCondition',  label: 'Cause Condition',  description: 'Apply a condition with a save throw (e.g., on hit, target saves DC 15 CON or Restrained).' },
]);

/**
 * Per-type allowed operations. The UI filters the operation dropdown using this
 * map so the player only sees operations the registry actually supports for the
 * picked type.
 */
export const OPERATIONS_BY_TYPE = Object.freeze({
  criticalRange:   ['min'],
  damageBonus:     ['add', 'add_dice'],
  attackBonus:        ['add'],
  weaponAttackBonus:  ['add'],
  spellAttackBonus:   ['add'],
  damageReduction: ['add'],
  ac:              ['add', 'set'],
  hp:              ['add'],
  speed:           ['add', 'set'],
  initiative:      ['add'],
  spellDC:         ['add'],
  carryCapacity:   ['multiply'],
  resistance:      ['grant', 'immunity'],
  sense:           ['grant'],
  proficiency:     ['grant'],
  advantage:       ['grant'],
  feature:         ['grant'],
  skill:           ['add'],
  extra_attack:    ['add'],
  perRestSpell: ['grant'],
  atWillSpell:  ['grant'],
  ritualSpell:  ['grant'],
  // [Spec A Task 10 — 2026-05-16]: Slot-recovery action only supports grant.
  slotRecoveryAction: ['grant'],
  // [Spec B Task 14 — 2026-05-16]: Action-surface effect types are all grant-only.
  //   Their value is an object the runtime consumes; the operation is purely a
  //   schema-shape marker.
  spendActionMenu:  ['grant'],
  reactionTrigger:  ['grant'],
  longRestPicker:   ['grant'],
  // [Spec C Task 1 — 2026-05-16/17]: triggeredEffect is grant-only — its value
  //   object carries triggerId + payload[] consumed by fireTriggeredEffects.
  triggeredEffect:  ['grant'],
  // [BATCH 10d / P1 D-18 — APR 2026]
  learnedSpell:    ['grant'],
  // spellModifier: 'set' overrides the spell's stat (e.g., set range to
  //   self for Spell Sniper-like distance overrides); 'add' stacks
  //   additively. Both are honored by effectCalculator.getEffectiveSpell*.
  spellModifier:   ['set', 'add'],
  hitDieRecovery:  ['add'],
  // [PHASE B / #028 — 2026-05-06]: causeCondition has only the 'apply'
  //   operation — the trigger field gates WHEN it fires (on_hit,
  //   on_save_fail, etc.) and the dc + saveType + duration carry the
  //   save-throw mechanics. No need for grant/set/add semantics.
  causeCondition:  ['apply'],
});

/**
 * Boolean condition flags currently provided by EffectCalculator._buildContext.
 * The Effect Builder dropdown shows one of these (or "(always)") so authors
 * don't make up flag names that the calculator never sets.
 *
 * Keep this list in sync with effectCalculator.js _buildContext. When a new
 * flag lands, add it here too.
 */
export const CONDITION_FLAGS = Object.freeze([
  { value: '',                                description: '(always — no condition)' },
  { value: 'raging',                          description: 'Player is in rage (Barbarian).' },
  { value: 'unarmored',                       description: 'Wearing no armor.' },
  { value: 'noShield',                        description: 'Not wielding a shield.' },
  { value: 'wieldingShield',                  description: 'Wielding a shield.' },
  { value: 'dualWielding',                    description: 'Two-weapon fighting (light melee in each hand).' },
  { value: 'wearingLightArmor',               description: 'Wearing light armor.' },
  { value: 'wearingMediumArmor',              description: 'Wearing medium armor.' },
  { value: 'wearingHeavyArmor',               description: 'Wearing heavy armor.' },
  { value: 'wieldingPolearm',                 description: 'Wielding a polearm (PAM-eligible).' },
  { value: 'sneakAttackActive',               description: 'Sneak Attack toggle is on (Rogue).' },
  { value: 'huntersMarkTargetActive',         description: 'Hunter\'s Mark target toggle is on.' },
  { value: 'attackingHexbladeCursedTarget',   description: 'Hexblade\'s Curse target toggle is on.' },
  { value: 'attackingSurprisedTarget',        description: 'Target is surprised (Assassin).' },
  { value: 'targetBelowFullHp',               description: 'Target is missing hit points.' },
  { value: 'attackingFavoredEnemy',           description: 'Attacking a favored enemy (Ranger).' },
  { value: 'attackingVowedTarget',            description: 'Vow of Enmity target toggle is on.' },
  { value: 'castingClericCantripDamage',      description: 'Casting a damaging Cleric cantrip.' },
  { value: 'castingDraconicElementSpell',     description: 'Casting a spell of your draconic element.' },
  { value: 'castingEvocationSpellDamage',     description: 'Casting a damaging evocation spell.' },
]);

/**
 * Suggested target-format hints per type. Free-text input in the UI, but the
 * placeholder educates the author on the canonical pattern. The registry
 * consumes targets like 'skill:perception', 'save:wisdom', 'tool:disguise_kit'.
 */
export const TARGET_HINT_BY_TYPE = Object.freeze({
  skill:       'skill:<name> (e.g., skill:perception)',
  proficiency: 'skill:<name> | save:<ability> | tool:<id> | weapon:<id> | armor:<class>',
  advantage:   'save:<keyword> | attack:<keyword> | check:<skill>',
  resistance:  '(use the value field for damage type)',
  sense:       '(use the value field — darkvision, blindsight, tremorsense, truesight)',
  feature:     '(no target — use value field for the flag id)',
  damageBonus: '(optional weapon filter, e.g., weapon:martial)',
  // [BATCH 10d / P1 D-18 — APR 2026]
  learnedSpell:   'spell name (e.g., Cure Wounds) — value field carries the level',
  spellModifier:  'spell name (target the spell) — value field carries the modifier',
  hitDieRecovery: '(no target — value carries the bonus, e.g., conMod for Durable)',
});

/**
 * [BATCH 10d / P1 D-18 — APR 2026]: Canonical autocomplete options per
 *   target type. The Effect Builder UI wires these into <datalist>
 *   elements so the player gets the right suggestion set without leaving
 *   the input. Free text is still accepted (the registry consumes the
 *   raw string), but the suggestion list keeps spelling honest and
 *   surfaces options that the registry actually understands.
 */
const SKILL_TARGETS = [
  'skill:acrobatics', 'skill:animal_handling', 'skill:arcana',
  'skill:athletics', 'skill:deception', 'skill:history',
  'skill:insight', 'skill:intimidation', 'skill:investigation',
  'skill:medicine', 'skill:nature', 'skill:perception',
  'skill:performance', 'skill:persuasion', 'skill:religion',
  'skill:sleight_of_hand', 'skill:stealth', 'skill:survival',
];
const SAVE_TARGETS = [
  'save:strength', 'save:dexterity', 'save:constitution',
  'save:intelligence', 'save:wisdom', 'save:charisma',
];
const TOOL_TARGETS = [
  "tool:smith's tools", "tool:thieves' tools", "tool:herbalism kit",
  "tool:tinker's tools", "tool:carpenter's tools", "tool:mason's tools",
  "tool:cook's utensils", "tool:jeweler's tools", "tool:leatherworker's tools",
  "tool:navigator's tools", "tool:painter's supplies", "tool:alchemist's supplies",
  "tool:brewer's supplies", "tool:calligrapher's supplies",
  "tool:cartographer's tools", "tool:cobbler's tools",
  "tool:glassblower's tools", "tool:potter's tools",
  "tool:weaver's tools", "tool:woodcarver's tools", "tool:poisoner's kit",
];
const ARMOR_TARGETS = ['armor:light', 'armor:medium', 'armor:heavy', 'armor:shield'];
const WEAPON_TARGETS = ['weapon:simple', 'weapon:martial'];

const ADVANTAGE_TARGETS = [
  // Save subjects
  'save:charm', 'save:fear', 'save:poison', 'save:magic',
  'save:strength', 'save:dexterity', 'save:constitution',
  'save:intelligence', 'save:wisdom', 'save:charisma',
  // Attack subjects
  'attack:next', 'attack:opportunity', 'attack:unseen',
  // Check subjects (skill checks)
  ...SKILL_TARGETS.map(s => s.replace('skill:', 'check:')),
];

export const TARGET_OPTIONS_BY_TYPE = Object.freeze({
  skill: Object.freeze(SKILL_TARGETS),
  proficiency: Object.freeze([
    ...SKILL_TARGETS, ...SAVE_TARGETS, ...TOOL_TARGETS,
    ...ARMOR_TARGETS, ...WEAPON_TARGETS,
  ]),
  advantage: Object.freeze(ADVANTAGE_TARGETS),
});

/**
 * Damage-type list used when the type dropdown picks `resistance`. Reuses the
 * shared DAMAGE_TYPES enum so we never drift from the rest of the runtime.
 */
export const DAMAGE_TYPE_OPTIONS = Object.freeze(
  Object.values(DAMAGE_TYPES).slice().sort()
);

/**
 * Senses commonly granted by subclass features. Free text is also accepted.
 */
export const SENSE_OPTIONS = Object.freeze([
  'darkvision',
  'blindsight',
  'tremorsense',
  'truesight',
]);

/**
 * [PHASE B / #028 — 2026-05-06]: causeCondition trigger options.
 * The trigger gates WHEN the condition fires:
 *   - `on_hit`: when the attached weapon/item lands a hit.
 *   - `on_save_fail`: when an opponent fails the carried save throw.
 *   - `on_take_damage`: when the wielder takes damage.
 *   - `while_attuned`: continuously while attuned/equipped (for items
 *     like a "while attuned, you have advantage on Charmed saves").
 */
export const TRIGGER_OPTIONS = Object.freeze([
  { value: 'on_hit',         label: 'On hit (target)' },
  { value: 'on_save_fail',   label: 'On save fail (target)' },
  { value: 'on_take_damage', label: 'On take damage (self)' },
  { value: 'while_attuned',  label: 'While attuned' },
]);

/**
 * [PHASE B / #028]: Save throw ability for causeCondition.
 * Lower-case ability keys to match the rest of the registry / engine
 * (see effectCalculator save targets — `save:strength` etc.).
 */
export const SAVE_TYPE_OPTIONS = Object.freeze([
  { value: 'strength',     label: 'Strength' },
  { value: 'dexterity',    label: 'Dexterity' },
  { value: 'constitution', label: 'Constitution' },
  { value: 'intelligence', label: 'Intelligence' },
  { value: 'wisdom',       label: 'Wisdom' },
  { value: 'charisma',     label: 'Charisma' },
]);

/**
 * [PHASE B / #028]: Duration enum for causeCondition.
 *   - `instant`: the condition is applied for a single trigger event then
 *     immediately resolves (a momentary stagger, not a persistent state).
 *   - `1_round`: until the start of the wielder's next turn.
 *   - `1_minute` / `10_minutes` / `1_hour`: standard PHB durations.
 *   - `until_save`: target re-rolls the save at the end of each of its
 *     turns; condition ends on success.
 */
export const DURATION_OPTIONS = Object.freeze([
  { value: 'instant',     label: 'Instant' },
  { value: '1_round',     label: '1 round' },
  { value: '1_minute',    label: '1 minute' },
  { value: '10_minutes',  label: '10 minutes' },
  { value: '1_hour',      label: '1 hour' },
  { value: 'until_save',  label: 'Until save' },
]);

/**
 * [Phase 5 Track B]: Formula options for perRestSpell.uses.
 * Stored as strings in the effect schema; resolved by
 * effectCalculator.getPerRestSpells() at query time against playerStats.
 */
export const USES_FORMULAS = Object.freeze([
  { value: 'wisMod',    label: 'WIS mod' },
  { value: 'strMod',    label: 'STR mod' },
  { value: 'dexMod',    label: 'DEX mod' },
  { value: 'conMod',    label: 'CON mod' },
  { value: 'intMod',    label: 'INT mod' },
  { value: 'chaMod',    label: 'CHA mod' },
  { value: 'profBonus', label: 'Proficiency Bonus' },
]);

/**
 * [Phase 4b BC-12]: Predicate that decides whether a serialized effect should
 *   be kept or dropped. We keep effects that explicitly declare a value (most
 *   types) AND effects that don't need one (feature flags and extra_attack
 *   markers). Drops half-authored "drafts" with no value/target/anything.
 */
export function effectIsAuthored(serialized) {
  if (!serialized || !serialized.type) return false;
  if (serialized.value !== undefined) return true;
  if (serialized.type === 'feature' || serialized.type === 'extra_attack') return true;
  return false;
}

/**
 * [Phase 4b BC-12]: Serialize a single feature's UI state (effects + choice
 *   block) into the registry's per-feature block shape used by
 *   srdSubclassFeatures.js / srdClassFeatures.js / srdRaceFeatures.js.
 *
 * Input  shape (UI):
 *   { effects: UIEffectRow[], hasChoices, choiceKey, choiceOptions: [{name, effects}] }
 *
 * Output shape (registry):
 *   { minLevel, effects?: Effect[], choiceKey?, choices?: { [name]: { effects } }, notes? }
 *
 * Returns NULL if the resulting block carries no mechanical wiring at all
 * (so callers can leave pure narrative features out of featureEffects entirely
 * without wasting registry slots).
 */
export function buildFeatureEffectsBlock({ minLevel, feature, notes }) {
  if (!feature) return null;
  const block = { minLevel };

  const builtEffects = (feature.effects || []).map(serializeEffect).filter(effectIsAuthored);
  if (builtEffects.length) block.effects = builtEffects;

  if (feature.hasChoices && feature.choiceKey && feature.choiceKey.trim()) {
    const choices = {};
    for (const opt of (feature.choiceOptions || [])) {
      const optName = opt.name && opt.name.trim();
      if (!optName) continue;
      const optEffects = (opt.effects || []).map(serializeEffect).filter(effectIsAuthored);
      if (optEffects.length === 0) continue;
      choices[optName] = { effects: optEffects };
    }
    if (Object.keys(choices).length > 0) {
      block.choiceKey = feature.choiceKey.trim();
      block.choices = choices;
      // [SUB-PLAN 4F GAP-4 — 2026-05-29]: Persist the player-facing prompt label
      //   so consumers (e.g. level-up wizard, character-sheet choice pickers) can
      //   display "Choose a Fighting Style:" instead of a generic default.
      if (feature.choicePromptLabel && feature.choicePromptLabel.trim()) {
        block.choicePromptLabel = feature.choicePromptLabel.trim();
      }
    }
  }

  if (notes && notes.trim()) block.notes = notes.trim();

  // Only emit when the block has mechanical wiring beyond minLevel + notes.
  if (block.effects || block.choices) return block;
  return null;
}

/**
 * [Phase 5 Track B]: Hydrate a stored registry effect back into a UI row shape.
 * Inverse of serializeEffect. Called when loading an existing subclass into
 * the Effect Builder so authors can edit previously-authored effects.
 *
 * Copies perRestSpell-specific fields (recharge, uses) in addition to the
 * base fields shared by all effect types.
 *
 * Pure function — exported here alongside serializeEffect so consumers share
 * one source of truth for the round-trip schema.
 */
export function hydrateEffect(e) {
  const hydrated = {
    type: e.type || 'criticalRange',
    operation: e.operation || 'min',
    value: e.value === undefined ? '' : String(e.value),
    target: e.target || '',
    condition: (e.condition && e.condition.when) || '',
  };

  // [Phase 5 Track B]: copy perRestSpell-specific fields.
  if (e.type === 'perRestSpell') {
    hydrated.recharge = e.recharge || 'long_rest';
    hydrated.uses = e.uses !== undefined ? e.uses : 1;
  }

  return hydrated;
}

/**
 * [Phase 4b BC-12]: Serialize a UI effect row into the registry's effect schema.
 *
 *   - Numeric strings are coerced to numbers (e.g., '19' → 19).
 *     Dice/formula strings ('1d8', 'rogueLevel/2:d6:roundUp') stay as strings.
 *   - Empty target / condition fields are dropped.
 *   - Condition string becomes { when: '<flag>' } so the registry's condition
 *     check applies (matches srdSubclassFeatures.js convention).
 *
 * Returns an effect object that the registry can register directly.
 *
 * Pure function — exposed here (rather than inlined in CustomSubclassCreator)
 * so tests can pin the schema without rendering the modal.
 */
export function serializeEffect(uiRow) {
  const out = { type: uiRow.type, operation: uiRow.operation };

  // [A49 AT-1c — 2026-06-03]: When value is a pickedAtLevelUp object (atWillSpell /
  //   perRestSpell with pickedAtLevelUp:true), preserve the object as-is so the
  //   registry receives the full pickConstraints shape. Previously String(value) would
  //   stringify the object to '[object Object]', losing className/featureId/forcedLevels.
  //   String/number paths are unchanged.
  // [A49 AT-2 — 2026-06-03]: slotRecoveryAction values are also objects; preserve
  //   the full shape (including invokeAnytime) using the same object-pass-through guard.
  if (
    uiRow.value !== undefined &&
    uiRow.value !== null &&
    typeof uiRow.value === 'object' &&
    (uiRow.value.pickedAtLevelUp || uiRow.type === 'slotRecoveryAction')
  ) {
    out.value = uiRow.value;
  } else {
    const rawValue = (uiRow.value === undefined || uiRow.value === null)
      ? ''
      : String(uiRow.value).trim();
    if (rawValue !== '') {
      const looksLikeInteger = /^-?\d+(\.\d+)?$/.test(rawValue);
      out.value = (looksLikeInteger && Number.isFinite(Number(rawValue)))
        ? Number(rawValue)
        : rawValue;
    }
  }

  if (uiRow.target && String(uiRow.target).trim()) {
    out.target = String(uiRow.target).trim();
  }
  if (uiRow.condition && String(uiRow.condition).trim()) {
    out.condition = { when: String(uiRow.condition).trim() };
  }

  // [Phase 5 Track B]: perRestSpell carries recharge + uses fields.
  if (uiRow.type === 'perRestSpell') {
    out.recharge = uiRow.recharge === 'short_rest' ? 'short_rest' : 'long_rest';
    const rawUses = uiRow.uses;
    if (typeof rawUses === 'string' && rawUses !== '') {
      out.uses = rawUses; // formula string: 'wisMod', 'profBonus', etc.
    } else {
      const num = typeof rawUses === 'number' ? rawUses : parseInt(rawUses, 10);
      out.uses = (Number.isFinite(num) && num > 0) ? num : 1;
    }
  }

  return out;
}
